/**
 * XlsxPassageDrawer
 *
 * In-app spreadsheet viewer for cited XLSX sources, built on Univer (OSS
 * preset-sheets-core, canvas-rendered). The cited worksheet is activated and
 * the cited cell range (A1 notation from index-time provenance) is selected,
 * scrolled into view, and tinted — cell-precise citation jumps that a PDF
 * preview of the workbook cannot offer.
 *
 * The workbook is parsed CLIENT-SIDE from the original file bytes via exceljs
 * (values only, v1 fidelity): Univer's own XLSX import is a Pro/server
 * feature, deliberately not used. Everything heavy (Univer, exceljs) is
 * dynamically imported so the chat bundle stays lean; the drawer's workbook is
 * an in-memory throwaway, so tinting the cited range mutates nothing real.
 * The header offers "open original" as the escape hatch (e.g. for .xls, which
 * exceljs cannot parse — those keep the LibreOffice PDF preview path).
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  GridOnOutlined as GridOnOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  buildOriginalDocumentUrl,
  viewOriginalDocument,
} from '../../utils/documents/documentHelpers';
import { log } from '../../utils/errors/logger';

export interface XlsxPassageTarget {
  documentId: string;
  title?: string;
  /** Cited worksheet name (index-time provenance). */
  sheetName?: string | null;
  /** Cited cell range in A1 notation, e.g. "A1:F18". */
  a1Range?: string | null;
}

interface XlsxPassageDrawerProps {
  target: XlsxPassageTarget | null;
  onClose: () => void;
}

/** Highlight fill for the cited range (matches the PDF passage highlight). */
const RANGE_FILL = '#ffe9a8';

/** "B7" -> zero-based {row: 6, col: 1}; null when unparsable. */
export function parseA1Cell(ref: string): { row: number; col: number } | null {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref.trim());
  if (!m) {
    return null;
  }
  let col = 0;
  for (const ch of m[1].toUpperCase()) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  return { row: parseInt(m[2], 10) - 1, col: col - 1 };
}

/** Flatten an exceljs cell value (rich text, formula results, dates) to a
 *  displayable primitive. */
function cellDisplayValue(value: unknown): string | number | boolean | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('result' in obj) {
      return cellDisplayValue(obj.result);
    }
    if (Array.isArray(obj.richText)) {
      return (obj.richText as Array<{ text?: string }>).map((r) => r.text ?? '').join('');
    }
    if ('text' in obj) {
      return cellDisplayValue(obj.text);
    }
  }
  return String(value);
}

export const XlsxPassageDrawer: React.FC<XlsxPassageDrawerProps> = ({ target, onClose }) => {
  const { t } = useTranslation();
  const open = target !== null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rangeMarked, setRangeMarked] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!target) {
      return undefined;
    }
    let cancelled = false;
    let dispose: (() => void) | null = null;
    setLoading(true);
    setError(null);
    setRangeMarked(false);

    const run = async () => {
      // 1. Original workbook bytes (cookie-authenticated, ACL-checked API).
      const response = await fetch(buildOriginalDocumentUrl(target.documentId), {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`document fetch failed: ${response.status}`);
      }
      const buffer = await response.arrayBuffer();
      if (cancelled) {
        return;
      }

      // 2. Parse values with exceljs into Univer's IWorkbookData shape.
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      if (cancelled) {
        return;
      }

      const sheets: Record<string, object> = {};
      const sheetOrder: string[] = [];
      workbook.eachSheet((ws, sheetId) => {
        const id = `sheet-${sheetId}`;
        const cellData: Record<number, Record<number, { v: string | number | boolean }>> = {};
        ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const v = cellDisplayValue(cell.value);
            if (v === null || v === '') {
              return;
            }
            (cellData[rowNumber - 1] ??= {})[colNumber - 1] = { v };
          });
        });
        sheetOrder.push(id);
        sheets[id] = {
          id,
          name: ws.name,
          rowCount: Math.max(ws.rowCount + 20, 100),
          columnCount: Math.max(ws.columnCount + 5, 26),
          cellData,
        };
      });
      if (sheetOrder.length === 0) {
        throw new Error('workbook has no sheets');
      }

      // 3. Boot Univer into the drawer container (dynamically imported — the
      //    sheet engine is multi-MB and must never sit in the chat bundle).
      const [{ createUniver, LocaleType }, corePreset, deDE] = await Promise.all([
        import('@univerjs/presets'),
        import('@univerjs/presets/preset-sheets-core'),
        import('@univerjs/presets/preset-sheets-core/locales/de-DE'),
        import('@univerjs/presets/lib/styles/preset-sheets-core.css'),
      ]);
      const container = containerRef.current;
      if (cancelled || !container) {
        return;
      }
      container.replaceChildren();
      const { univer, univerAPI } = createUniver({
        locale: LocaleType.DE_DE,
        locales: { [LocaleType.DE_DE]: deDE.default },
        presets: [corePreset.UniverSheetsCorePreset({ container })],
      });
      dispose = () => univer.dispose();

      univerAPI.createWorkbook({
        id: `cited-${target.documentId}`,
        name: target.title ?? 'Workbook',
        sheetOrder,
        sheets,
      } as Parameters<typeof univerAPI.createWorkbook>[0]);

      // 4. Jump to the cited worksheet + range.
      const fWorkbook = univerAPI.getActiveWorkbook();
      const fSheet =
        (target.sheetName ? fWorkbook?.getSheetByName(target.sheetName) : null) ??
        fWorkbook?.getActiveSheet() ??
        null;
      if (fWorkbook && fSheet) {
        fWorkbook.setActiveSheet(fSheet);
        const range = target.a1Range?.trim();
        if (range) {
          try {
            const fRange = fSheet.getRange(range);
            fRange.setBackgroundColor(RANGE_FILL);
            fRange.activate();
            const startCell = parseA1Cell(range.split(':')[0]);
            const sheetWithScroll = fSheet as unknown as {
              scrollToCell?: (row: number, col: number) => void;
            };
            if (startCell && typeof sheetWithScroll.scrollToCell === 'function') {
              sheetWithScroll.scrollToCell(startCell.row, startCell.col);
            }
            setRangeMarked(true);
          } catch (e) {
            log.warn('XlsxPassageDrawer: could not mark cited range', {
              range,
              error: String(e),
            });
          }
        }
      }
      log.user.action('xlsx_passage_viewed', 'XlsxPassageDrawer', {
        document_id: target.documentId,
        sheet: target.sheetName ?? null,
        range: target.a1Range ?? null,
      });
      setLoading(false);
    };

    run().catch((e: unknown) => {
      if (!cancelled) {
        log.error('XlsxPassageDrawer: failed to load workbook', { error: String(e) });
        setError(
          t('chat.sources.xlsxViewer.loadError', {
            defaultValue: 'Die Arbeitsmappe konnte nicht geladen werden.',
          })
        );
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      dispose?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only per target
  }, [target]);

  return (
    <Drawer
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100vw', md: 'min(1080px, 94vw)' } } } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #dde3ea',
        }}
      >
        <GridOnOutlinedIcon sx={{ color: '#1d6f42' }} />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '1 1 auto',
          }}
        >
          {target?.title ?? ''}
        </Typography>
        {rangeMarked && target?.a1Range && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${target.sheetName ? `${target.sheetName} · ` : ''}${target.a1Range}`}
          />
        )}
        <Tooltip title={t('chat.sources.openDocument', { defaultValue: 'Open document' })}>
          <IconButton
            size="small"
            onClick={() => {
              if (target) {
                void viewOriginalDocument(target.documentId);
              }
            }}
            aria-label="open in new tab"
          >
            <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Box>
      <Box sx={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />
      </Box>
    </Drawer>
  );
};
